/**
 * Visitor Context
 *
 * Lightweight visitor profile cached in a cookie so smart content slots
 * can be personalized without calling HubSpot on every request.
 *
 * On first visit we look up the visitor by their `hubspotutk` cookie via
 * `/api/visitor-context`, then persist the normalized result in our own
 * `scw_vctx` cookie (non-HttpOnly so the client can also read it). The
 * cookie is the single source of truth — no DB rows per anonymous visitor.
 */

export const VISITOR_CONTEXT_COOKIE = 'scw_vctx';
export const HUBSPOT_UTK_COOKIE = 'hubspotutk';

/** 7 days — matches HubSpot's utk cookie TTL and keeps fetch cost low. */
export const VISITOR_CONTEXT_TTL_SECONDS = 7 * 24 * 60 * 60;
/** Shorter TTL for anonymous visitors so we retry if they later fill a form. */
export const VISITOR_CONTEXT_ANON_TTL_SECONDS = 24 * 60 * 60;

export interface VisitorContext {
  /** HubSpot `industry` property, lowercased (e.g. "manufacturing"). */
  industry: string | null;
  /** HubSpot `lifecyclestage` (e.g. "customer", "lead"). */
  lifecycleStage: string | null;
  /** ISO country code (e.g. "US"). */
  country: string | null;
  /** HubSpot `company` name if known. */
  company: string | null;
  /** True once we've linked the visitor to a HubSpot contact. */
  isKnownContact: boolean;
  /** Unix seconds when this context was written, for debugging. */
  fetchedAt: number;
}

export function emptyAnonContext(): VisitorContext {
  return {
    industry: null,
    lifecycleStage: null,
    country: null,
    company: null,
    isKnownContact: false,
    fetchedAt: Math.floor(Date.now() / 1000),
  };
}

/**
 * Parse a cookie string (single cookie value, already URL-decoded) into a
 * VisitorContext, or return null if the payload is invalid.
 */
export function parseVisitorContextCookie(raw: string | undefined | null): VisitorContext | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const obj = parsed as Record<string, unknown>;
    const stringOrNull = (v: unknown): string | null =>
      typeof v === 'string' && v.length > 0 ? v : null;
    return {
      industry: stringOrNull(obj.industry),
      lifecycleStage: stringOrNull(obj.lifecycleStage),
      country: stringOrNull(obj.country),
      company: stringOrNull(obj.company),
      isKnownContact: obj.isKnownContact === true,
      fetchedAt: typeof obj.fetchedAt === 'number' ? obj.fetchedAt : 0,
    };
  } catch {
    return null;
  }
}

export function serializeVisitorContextCookie(ctx: VisitorContext): string {
  return JSON.stringify(ctx);
}

/**
 * Read a named cookie from the browser `document.cookie` string.
 * Returns the raw decoded value or null.
 */
export function readClientCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}
